'use client';

import { useState, useEffect } from 'react';
import { Box, CircularProgress, Grid, TextField, Stack, Button, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import { getFuelTypeById, createFuelType, updateFuelType } from '../../../stores/fuelTypeStore';
import { formatUpperCase } from '../../../utils/formatTitleCase';

const categoryOptions = ['Gasoline/Bensin', 'Diesel/Solar', 'Gas/LPG', 'Electric/Listrik', 'Hydrogen/Hidrogen(H2)'];

const getUomByCategory = (category) => {
  if (category === 'Gasoline/Bensin' || category === 'Diesel/Solar') {
    return 'Litre/Liter';
  } else if (category === 'Gas/LPG' || category === 'Hydrogen/Hidrogen(H2)') {
    return 'Kg/Kilogram';
  } else if (category === 'Electric/Listrik') {
    return 'KWh/Kilowatt Hour';
  }
  return '';
};

const MasterFuelTypeAddEdit = ({ open, id, onClose, onSuccess }) => {
  const [initialValues, setInitialValues] = useState({
    name: '',
    code: '',
    category: '',
    uom_type: '',
    description: ''
  });
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const notify = useNotifier();

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchInitial = async () => {
      setLoading(true);
      try {
        if (id) {
          const data = await getFuelTypeById(id);
          if (!mounted) return;
          if (data) {
            setInitialValues({
              name: data.name ?? '',
              code: data.code ?? '',
              category: data.category ?? '',
              uom_type: data.uom_type ?? '',
              description: data.description ?? ''
            });
          }
        } else {
          setInitialValues({
            name: '',
            code: '',
            category: '',
            uom_type: '',
            description: ''
          });
        }
      } catch (err) {
        console.error('Failed to fetch fuel type:', err);
        notify({ message: t('notify.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchInitial();

    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.nameRequired', 'Name is required')),
    code: Yup.string().required(t('validation.codeRequired', 'Code is required')),
    category: Yup.string().required(t('validation.categoryRequired', 'Category is required')),
    uom_type: Yup.string().required(t('validation.uomTypeRequired', 'UOM Type is required'))
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: formatUpperCase(values.name),
          code: formatUpperCase(values.code),
          category: values.category,
          uom_type: values.uom_type,
          description: values.description
        };

        let response;
        if (id) {
          response = await updateFuelType(id, payload);
          notify({ message: t('notify.update', 'Updated successfully'), severity: 'success' });
        } else {
          response = await createFuelType(payload);
          notify({ message: t('notify.create', 'Created successfully'), severity: 'success' });
        }

        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }

        formik.resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={id ? t('general.edit', 'Edit') + ' Fuel Type' : t('general.add', 'Add') + ' Fuel Type'}
      maxWidth="xs"
      fullHeight={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', maxWidth: 920 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="medium"
                    id="name"
                    name="name"
                    label={t('general.name', 'Name') + ' *'}
                    value={formik.values.name}
                    onChange={(e) => {
                      formik.setFieldValue('name', formatUpperCase(e.target.value));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && !!formik.errors.name}
                    helperText={formik.touched.name && formik.errors.name}
                  />

                  <TextField
                    fullWidth
                    size="medium"
                    id="code"
                    name="code"
                    label={t('masterUser.code', 'Code') + ' *'}
                    value={formik.values.code}
                    onChange={(e) => {
                      formik.setFieldValue('code', formatUpperCase(e.target.value));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.code && !!formik.errors.code}
                    helperText={formik.touched.code && formik.errors.code}
                  />

                  <TextField
                    select
                    fullWidth
                    size="medium"
                    id="category"
                    name="category"
                    label={t('general.category', 'Category') + ' *'}
                    value={formik.values.category}
                    onChange={(e) => {
                      const selectedCat = e.target.value;
                      formik.setFieldValue('category', selectedCat);
                      const uom = getUomByCategory(selectedCat);
                      if (uom) {
                        formik.setFieldValue('uom_type', uom);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.category && !!formik.errors.category}
                    helperText={formik.touched.category && formik.errors.category}
                  >
                    {categoryOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    size="medium"
                    id="uom_type"
                    name="uom_type"
                    label={t('general.uomType', 'UOM Type') + ' *'}
                    value={formik.values.uom_type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.uom_type && !!formik.errors.uom_type}
                    helperText={formik.touched.uom_type && formik.errors.uom_type}
                    disabled={true}
                  />

                  <TextField
                    fullWidth
                    size="medium"
                    multiline
                    rows={3}
                    id="description"
                    name="description"
                    label={t('general.description', 'Description')}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.description && !!formik.errors.description}
                    helperText={formik.touched.description && formik.errors.description}
                  />
                </Stack>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterFuelTypeAddEdit;
